'use client'

import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import type { ReviewSummary } from '@/lib/ap/review'
import { approverBriefing, badgeText, REVIEWER_NAV, reviewerNavActive } from '@/lib/review-view'
import { cn } from '@/lib/utils'

const ICONS: Record<string, React.ElementType> = {
  waiting: Icons.checkCircle,
  decided: Icons.history,
}

/**
 * The approver's left sidebar (plan 10 C6), modelled on the IT Command Center
 * employee rail: a short briefing at the top (waiting, on hold, decided this
 * week, oldest waiting), then "My approvals" with the waiting count and
 * "Approved by me" (the approver's decided history). Nothing else: no
 * policies, insights or Auto Graph. The counts come from the shell's
 * `/api/review/summary` poll, so the sidebar adds no request of its own.
 */
export function ReviewSidebar({
  summary,
  loading,
  error,
  onRetry,
}: {
  summary: ReviewSummary | null
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  const pathname = usePathname()
  const filter = useSearchParams().get('filter')
  const facts = approverBriefing(summary)

  return (
    <div className='flex flex-col gap-6'>
      <section aria-labelledby='approver-briefing' className='rounded-xl border border-hairline bg-card p-4 shadow-soft'>
        <h2 id='approver-briefing' className='text-micro uppercase tracking-widest text-muted-foreground'>
          Your briefing
        </h2>
        {summary ? (
          <dl className='mt-3 grid grid-cols-2 gap-x-3 gap-y-2 lg:grid-cols-1'>
            {facts.map((fact) => (
              <div key={fact.label} className='flex items-baseline justify-between gap-2'>
                <dt className='text-sm text-muted-foreground'>{fact.label}</dt>
                <dd className='font-display text-base font-semibold tabular-nums text-foreground'>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : loading || !error ? (
          <div className='mt-3 space-y-2' aria-busy='true' aria-label='Loading your briefing'>
            <Skeleton className='h-5 w-full' />
            <Skeleton className='h-5 w-full' />
            <Skeleton className='h-5 w-2/3' />
          </div>
        ) : (
          <div role='alert' className='mt-3 space-y-2 text-sm text-danger-strong'>
            <p>Could not load your counts.</p>
            <Button size='sm' variant='outline' className='min-h-11 md:min-h-9' onClick={onRetry}>
              Retry
            </Button>
          </div>
        )}
      </section>

      <nav aria-label='Approvals'>
        <ul className='flex gap-2 lg:flex-col'>
          {REVIEWER_NAV.map((item) => {
            const active = reviewerNavActive(pathname, filter, item.key)
            const Icon = ICONS[item.key] ?? Icons.inbox
            const badge = item.showsWaiting ? badgeText(summary?.waiting ?? 0) : ''
            return (
              <li key={item.key} className='flex-1 lg:flex-none'>
                <Link
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  aria-label={badge ? `${item.label}, ${badge} waiting` : undefined}
                  className={cn(
                    'flex min-h-11 items-center gap-2.5 rounded-lg px-3 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                  )}
                >
                  <Icon className='h-4 w-4 shrink-0' aria-hidden='true' />
                  <span className='flex-1 truncate'>{item.label}</span>
                  {badge && (
                    <span
                      aria-hidden='true'
                      className='flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-cornflower px-1.5 text-xs font-semibold text-brand-navy'
                    >
                      {badge}
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </div>
  )
}
